import { toast } from 'sonner';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as cartApi from '@/shared/services/cart.service';
import { applyCoupon, removeCoupon } from '@/shared/services/coupon.service';
import { authServices } from '@/shared/services/auth.services';
import type { CartItemDelivery, CartMeta as ApiCartMeta } from "@/shared/services/cart.service";
import { cartLinesEqual } from '@/shared/utils/cartLineMatch';
import { trackMetaAddToCart } from '@/shared/analytics/metaPixel';
// Imported off the module rather than the `@/store` barrel: the barrel re-exports
// this file, so going through it would close an import cycle.
import { useLocationStore } from './locationStore';
import { useGuestOfferTimerStore } from './guestOfferTimerStore';

export interface CartItem {
    productId: string;
    variantId?: string | null;
    /** Fastest delivery option for the pincode the cart was last fetched with. */
    delivery?: CartItemDelivery | null;
    /** Compatible car this line is for (catalog `cars.id`), from "Select Your Car". */
    carId?: string | null;
    quantity: number;
    installation?: { id: string; name: string; price: number; timeTaken: string } | null;
    installationAvailable?: boolean;
    availableInstallationPackages?: Array<{
        id: string;
        name: string;
        description: string;
        price: number;
        timeTaken: string;
    }>;
    selectedOptions?: Record<string, string> | null;
    productDetails: {
        id: string;
        slug: string;
        title: string;
        sku: string;
        primary_image: string;
        price: number;
        compare_at_price: number;
        discount_percentage: string;
        in_stock: boolean;
        stock: number;
        brand_name: string;
        /** When true, cart line shows masked selling price and hides the discount badge. */
        hide_coupon_section?: boolean;
    };
}

export interface PendingCoupon {
    code: string;
    discount_type: string;
    discount_value: number;
    max_discount_cap: number;
    min_cart_value: number;
    discount_amount: number;
}

interface CartState {
    items: CartItem[];
    meta: ApiCartMeta | null;
    pendingCoupon: PendingCoupon | null;
    isOpen: boolean;
    isLoading: boolean;
    error: string | null;
    /** Lines whose quantity sync is in flight, keyed by product + variant. Signed-in only. */
    pendingQuantityLines: Record<string, boolean>;

    /**
     * Resolves `true` when the item is actually in the cart (guest add, or server add succeeded), `false` if the server rejected it.
     * `keepCoupon` leaves a server-applied code in place — used when the shopper armed the product coupon first.
     */
    addItem: (
        item: CartItem,
        options?: {
            optimistic?: boolean
            track?: boolean
            keepCoupon?: boolean
            /** Fired after a server rejection, once the local cart has been refreshed. */
            onRejected?: (error: unknown) => void
        },
    ) => Promise<boolean>;
    removeItem: (productId: string, variantId?: string | null) => Promise<void>;
    updateQuantity: (productId: string, quantity: number, variantId?: string | null) => Promise<void>;
    changeVariant: (productId: string, oldVariantId: string, newVariantId: string, newProductDetails: CartItem['productDetails'], quantity: number) => Promise<void>;
    addInstallation: (productId: string, variantId: string | null | undefined, packageId: string) => Promise<void>;
    removeInstallation: (productId: string, variantId: string | null | undefined) => Promise<void>;
    setItemCar: (productId: string, variantId: string | null | undefined, carId: string | null) => void;
    setPendingCoupon: (coupon: PendingCoupon | null) => void;
    resetCartLocal: () => void;
    clearCart: () => Promise<void>;
    toggleCart: () => void;
    openCart: () => void;
    closeCart: () => void;
    fetchCart: (silent?: boolean) => Promise<void>;
    syncWithApi: (options?: { silent?: boolean }) => Promise<void>;

    getTotalItems: () => number;
    getTotalPrice: () => number;
    getTotalSavings: () => number;
}

function deriveLocalMeta(items: CartItem[]): ApiCartMeta {
    const totalItems = items.reduce((sum, item) => sum + (item?.quantity || 0), 0);
    const subtotal = items.reduce((sum, item) => {
        if (!item?.productDetails) return sum;
        return sum + (item.productDetails.price || 0) * (item.quantity || 0);
    }, 0);

    return {
        total_items: totalItems,
        subtotal: subtotal.toFixed(2),
        currency: 'INR',
        isDiscountApplicable: false,
        discountAmount: 0,
        couponCode: null,
        installation_total: 0,
        shipping_fee: 0,
        final_total: subtotal,
        total_savings: 0,
        total_cashback: 0,
        delivery: null,
    };
}

/**
 * The header pincode, read at request time. It governs browsing only, so the
 * cart carries it on every fetch while checkout deliberately does not.
 */
function browsingPincode(): string | null {
    return useLocationStore.getState().location?.pincode ?? null;
}

function sanitizeItems(items: CartItem[]): CartItem[] {
    return Array.isArray(items) ? items.filter((item) => item && item.productDetails) : [];
}

function buildLocalCartState(items: CartItem[]) {
    return {
        items,
        meta: deriveLocalMeta(items),
        error: null,
    };
}

/**
 * The BE doesn't persist/return the per-line car yet, so carry the locally
 * chosen `carId` across any API fetch (matched by product + variant). Without
 * this, every cart refresh / page load would wipe the selected car.
 */
function preserveLocalCarIds(apiItems: CartItem[], prevItems: CartItem[]): CartItem[] {
    return apiItems.map((item) => {
        const match = prevItems.find((p) => cartLinesEqual(p, item));
        return match?.carId ? { ...item, carId: match.carId } : item;
    });
}

async function refreshCartFromApi(
    set: (partial: Partial<CartState>) => void,
    get?: () => CartState,
): Promise<void> {
    const response = await cartApi.fetchCart({ pincode: browsingPincode() });
    const merged = preserveLocalCarIds(sanitizeItems(response.data), get?.().items ?? []);

    set({
        items: merged,
        meta: response.meta || deriveLocalMeta(merged),
        error: null,
    });
}
async function dropCouponOnEmptyCart(
    set: (partial: Partial<CartState>) => void,
    get: () => CartState,
): Promise<void> {
    if (get().items.length > 0) return;

    if (!authServices.isAuthenticated()) {
        if (get().pendingCoupon) set({ pendingCoupon: null });
        return;
    }
    try {
        await removeCoupon();
    } catch (error) {
        const statusCode = (error as { statusCode?: number } | null)?.statusCode;
        if (statusCode !== 404) {
            console.error('Failed to drop the coupon on an emptied cart:', error);
        }
    }

    await refreshCartFromApi(set, get).catch(() => {});
}

const COUPON_DROPPED_MESSAGE = 'Your coupon could not be applied to this account';

const QUANTITY_SYNC_DEBOUNCE_MS = 400;
const quantitySyncTimers = new Map<string, ReturnType<typeof setTimeout>>();

// `fetchCart` has no request de-duping: the mount-time fetch and a coupon
// apply/remove's own fetch can land in flight together, and nothing stopped
// whichever response arrived *last* from winning even if it was issued
// *first* — a slow stale fetch could silently overwrite a just-applied
// coupon's discount. Only the response matching the most recently issued
// request is allowed to write to the store.
let fetchCartRequestId = 0;
let latestFetchCartIsSilent = false;

function cartLineKey(productId: string, variantId?: string | null): string {
    return `${productId}::${variantId ?? ''}`;
}

function cancelQuantitySync(key: string): void {
    const timer = quantitySyncTimers.get(key);
    if (timer) {
        clearTimeout(timer);
        quantitySyncTimers.delete(key);
    }
}

function setLinePending(
    key: string,
    pending: boolean,
    set: (partial: Partial<CartState>) => void,
    get: () => CartState,
): void {
    const next = { ...get().pendingQuantityLines };
    if (pending) next[key] = true;
    else delete next[key];
    set({ pendingQuantityLines: next });
}

function scheduleQuantitySync(
    productId: string,
    variantId: string | null | undefined,
    set: (partial: Partial<CartState>) => void,
    get: () => CartState,
): void {
    const key = cartLineKey(productId, variantId);
    cancelQuantitySync(key);

    const timer = setTimeout(() => {
        quantitySyncTimers.delete(key);
        // Read the *latest* quantity at fire time, so only the final value is sent.
        const line = get().items.find((i) => cartLinesEqual(i, { productId, variantId }));
        if (!line || line.quantity <= 0) return;

        // Marks the line busy so its stepper disables while the call is in flight —
        // which is also what stops a second, overlapping sync from starting.
        setLinePending(key, true, set, get);

        cartApi.updateCartItem(productId, line.quantity, { variantId })
            .then(() => refreshCartFromApi(set, get))
            .catch(async (error) => {
                console.warn('Cart update failed:', error?.message || 'API unavailable');
                await refreshCartFromApi(set, get).catch(() => {});
            })
            .finally(() => setLinePending(key, false, set, get));
    }, QUANTITY_SYNC_DEBOUNCE_MS);

    quantitySyncTimers.set(key, timer);
}

export const useCartStore = create<CartState>()(
    persist(
        (set, get) => ({
            items: [],
            meta: null,
            pendingCoupon: null,
            isOpen: false,
            isLoading: false,
            error: null,
            pendingQuantityLines: {},

            addItem: async (item, options) => {
                const optimistic = options?.optimistic ?? true;
                // Every user-facing add-to-cart path funnels through here, so this is
                // the one place the Meta event has to fire. Restoring a cart after a
                // failed payment replays adds the shopper never made, so that caller
                // opts out.
                const track = options?.track ?? true;
                const authed = authServices.isAuthenticated();

                const reportAddToCart = () => {
                    if (!track) return;
                    // A replayed restore isn't a new add, so it leaves the order alone —
                    // checkout's offer timer follows the product added last.
                    useGuestOfferTimerStore.getState().markAddedToCart(item.productDetails?.slug);
                    trackMetaAddToCart({
                        productId: item.productId,
                        sku: item.productDetails?.sku,
                        price: item.productDetails?.price ?? 0,
                        quantity: item.quantity,
                        title: item.productDetails?.title,
                    });
                };

                // Guests have no server validation, so the local add is the only source
                // of truth and must always apply. Authed callers can opt out
                // (optimistic: false) so a server-rejected add — e.g. a variant product
                // added from the wishlist without a variant — doesn't briefly flicker the
                // cart count up and back down (ES-251 follow-up).
                if (optimistic || !authed) {
                    set((state) => {
                        const existingItem = state.items.find((i) =>
                            cartLinesEqual(i, item),
                        );
                        const items = existingItem
                            ? state.items.map((i) =>
                                cartLinesEqual(i, item)
                                    // Latest selection wins: refresh the car if a new one was picked.
                                    // `!== undefined` so an explicit null (cleared) still applies; undefined keeps existing.
                                    ? { ...i, quantity: i.quantity + item.quantity, carId: item.carId !== undefined ? item.carId : i.carId }
                                    : i
                            )
                            : [...state.items, item];

                        return buildLocalCartState(items);
                    });
                }

                if (!authed) {
                    reportAddToCart();
                    return true;
                }

                try {
                    const response = await cartApi.addToCart({
                        productId: item.productId,
                        quantity: item.quantity,
                        variantId: item.variantId,
                        carId: item.carId,
                    });
                    await refreshCartFromApi(set, get);
                    // Only after the server accepted it — a rejected add (e.g. a variant
                    // product added without a variant) must not report a conversion.
                    reportAddToCart();
                    // Adding a line must not attach a coupon unless the shopper armed one
                    // on the product page. A leftover armed coupon can still resolve
                    // server-side on this POST — drop it so a plain add stays uncoupled.
                    if (response.couponApplied && !options?.keepCoupon) {
                        try {
                            await removeCoupon();
                            await refreshCartFromApi(set, get);
                        } catch (error) {
                            console.error('Failed to clear auto-applied coupon after add:', error);
                        }
                    }
                    return true;
                } catch (error) {
                    console.error('Failed to add item to cart API:', error);
                    await refreshCartFromApi(set, get).catch(() => {});
                    options?.onRejected?.(error);
                    return false;
                }
            },

            removeItem: async (productId, variantId) => {
                set((state) => buildLocalCartState(
                    state.items.filter(
                        (i) => !cartLinesEqual(i, { productId, variantId }),
                    ),
                ));

                if (!authServices.isAuthenticated()) {
                    await dropCouponOnEmptyCart(set, get);
                    return;
                }

                try {
                    await cartApi.removeCartItem(productId, { variantId });
                    await refreshCartFromApi(set, get);
                    await dropCouponOnEmptyCart(set, get);
                } catch (error) {
                    console.error('Failed to remove item from cart API:', error);
                }
            },

            updateQuantity: async (productId, quantity, variantId) => {
                if (quantity <= 0) {
                    cancelQuantitySync(cartLineKey(productId, variantId));
                    await get().removeItem(productId, variantId);
                    return;
                }

                // Optimistic local update — instant UI on every click.
                set((state) => buildLocalCartState(
                    state.items.map((i) =>
                        cartLinesEqual(i, { productId, variantId })
                            ? { ...i, quantity }
                            : i
                    ),
                ));

                if (!authServices.isAuthenticated()) {
                    return;
                }

                // Debounce the network sync so rapid clicks send one final request.
                scheduleQuantitySync(productId, variantId, set, get);
            },

            changeVariant: async (productId, oldVariantId, newVariantId, newProductDetails, quantity) => {
                const oldLine = { productId, variantId: oldVariantId };
                const newLine = { productId, variantId: newVariantId };
                const carId = get().items.find((i) => cartLinesEqual(i, oldLine))?.carId;
                set((state) => {
                    // Already in the cart at the new variant: fold into that line, as the server does.
                    const merged = state.items.some((i) => cartLinesEqual(i, newLine));
                    const items = merged
                        ? state.items
                            .filter((i) => !cartLinesEqual(i, oldLine))
                            .map((i) => cartLinesEqual(i, newLine) ? { ...i, quantity: i.quantity + quantity } : i)
                        // Swap in-place so position doesn't change
                        : state.items.map((i) =>
                            cartLinesEqual(i, oldLine)
                                ? { ...i, variantId: newVariantId, productDetails: newProductDetails }
                                : i
                        );
                    return buildLocalCartState(items);
                });

                if (!authServices.isAuthenticated()) return;

                try {
                    await cartApi.removeCartItem(productId, { variantId: oldVariantId });
                    await cartApi.addToCart({ productId, quantity, variantId: newVariantId, carId });
                    await refreshCartFromApi(set, get);
                } catch (error) {
                    console.error('Failed to change variant:', error);
                    await refreshCartFromApi(set, get).catch(() => {});
                }
            },

            addInstallation: async (productId, variantId, packageId) => {
                const item = get().items.find((i) => cartLinesEqual(i, { productId, variantId }));
                if (!item) return;
                try {
                    await cartApi.updateCartItem(productId, item.quantity, { variantId, installationPackageId: packageId });
                    await refreshCartFromApi(set, get);
                } catch (error) {
                    console.error('Failed to add installation:', error);
                }
            },

            removeInstallation: async (productId, variantId) => {
                const item = get().items.find((i) => cartLinesEqual(i, { productId, variantId }));
                if (!item) return;
                try {
                    await cartApi.updateCartItem(productId, item.quantity, { variantId, installationPackageId: null });
                    await refreshCartFromApi(set, get);
                } catch (error) {
                    console.error('Failed to remove installation:', error);
                }
            },

            setItemCar: (productId, variantId, carId) => {
                // Client-side only — BE doesn't persist the per-line car yet.
                // Preserved across API refreshes via refreshCartFromApi().
                set({
                    items: get().items.map((i) =>
                        cartLinesEqual(i, { productId, variantId })
                            ? { ...i, carId }
                            : i,
                    ),
                });
            },

            setPendingCoupon: (coupon) => set({ pendingCoupon: coupon }),

            resetCartLocal: () => {
                set({ items: [], meta: null, pendingCoupon: null, isOpen: false, isLoading: false, error: null });
            },

            clearCart: async () => {
                set({ items: [], meta: deriveLocalMeta([]), error: null, pendingCoupon: null });

                if (!authServices.isAuthenticated()) {
                    return;
                }

                try {
                    await cartApi.clearCart();
                    await dropCouponOnEmptyCart(set, get);
                } catch (error) {
                    console.error('Failed to clear cart API:', error);
                }
            },

            toggleCart: () => set({ isOpen: !get().isOpen }),
            openCart: () => set({ isOpen: true }),
            closeCart: () => set({ isOpen: false }),

            fetchCart: async (silent = false) => {
                const requestId = ++fetchCartRequestId;
                latestFetchCartIsSilent = silent;
                const isSuperseded = () => {
                    if (requestId === fetchCartRequestId) return false;
                    // A superseded loud fetch owns the spinner; a silent successor never clears it.
                    if (!silent && latestFetchCartIsSilent) set({ isLoading: false });
                    return true;
                };

                if (!authServices.isAuthenticated()) {
                    const items = sanitizeItems(get().items);
                    if (requestId !== fetchCartRequestId) return;
                    set((state) => ({
                        items,
                        meta: deriveLocalMeta(items),
                        ...(silent ? {} : { isLoading: false }),
                        error: null,
                    }));
                    return;
                }

                if (!silent) set({ isLoading: true, error: null });
                try {
                    const prev = get().items;
                    const response = await cartApi.fetchCart({ pincode: browsingPincode() });
                    if (isSuperseded()) return;
                    const items = preserveLocalCarIds(sanitizeItems(response.data), prev);
                    set((state) => ({
                        items,
                        meta: response.meta || deriveLocalMeta(items),
                        ...(silent ? {} : { isLoading: false }),
                        error: null,
                    }));
                } catch (error) {
                    console.error('Failed to fetch cart:', error);
                    if (isSuperseded()) return;
                    if (!silent) set({ isLoading: false, error: 'Failed to fetch cart' });
                }
            },

            syncWithApi: async (options) => {
                if (!authServices.isAuthenticated()) {
                    return;
                }

                set({ isLoading: true, error: null });
                const localItems = sanitizeItems(get().items);
                const silent = options?.silent ?? false;

                try {
                    // A line the server refuses — a variant product added while logged
                    // out carries no variantId, so `POST /cart/items` 400s — must not be
                    // dropped: the replay's own result is the only record it ever existed.
                    const rejected: CartItem[] = [];

                    if (localItems.length > 0) {
                        const results = await Promise.allSettled(
                            localItems.map((item) =>
                                cartApi.addToCart(
                                    {
                                        productId: item.productId,
                                        quantity: item.quantity,
                                        variantId: item.variantId,
                                        carId: item.carId,
                                    },
                                    { silent },
                                ),
                            ),
                        );

                        results.forEach((result, index) => {
                            if (result.status === 'rejected') rejected.push(localItems[index]);
                        });
                    }

                    // A guest's coupon was only priced as a brand-new customer; re-check it
                    // against the real account. A refusal (commonly "already used") drops it
                    // with the server's own message.
                    const pendingCoupon = get().pendingCoupon;
                    if (pendingCoupon) {
                        set({ pendingCoupon: null });
                        try {
                            const result = await applyCoupon(pendingCoupon.code);
                            if (!result.valid) toast.error(result.message || COUPON_DROPPED_MESSAGE);
                        } catch (error) {
                            const message = (error as { error?: unknown } | null)?.error;
                            toast.error(typeof message === 'string' && message.trim() ? message : COUPON_DROPPED_MESSAGE);
                        }
                    }

                    const response = await cartApi.fetchCart({ silent, pincode: browsingPincode() });
                    const serverItems = preserveLocalCarIds(sanitizeItems(response.data), localItems);
                    // Only the ones the server really doesn't have — a rejection can also
                    // mean "already there" for a line another call landed.
                    const kept = rejected.filter(
                        (item) => !serverItems.some((serverItem) => cartLinesEqual(serverItem, item)),
                    );
                    const items = [...serverItems, ...kept];

                    set({
                        items,
                        meta: response.meta || deriveLocalMeta(items),
                        isLoading: false,
                        error: null,
                    });

                    if (kept.length > 0) {
                        const names = kept
                            .map((item) => item.productDetails?.title)
                            .filter(Boolean)
                            .join(', ');
                        toast.error(
                            names
                                ? `Choose an option for ${names} to move it into your cart`
                                : 'Some items need an option selected before they move into your cart',
                        );
                    }

                    cartApi.clearSessionId();
                } catch (error) {
                    console.error('Failed to merge cart:', error);
                    set({ isLoading: false, error: 'Failed to merge cart' });
                }
            },

            getTotalItems: () => {
                const items = get().items;
                return Array.isArray(items) ? items.reduce((sum, item) => sum + (item?.quantity || 0), 0) : 0;
            },
            getTotalPrice: () => {
                const items = get().items;
                return Array.isArray(items) ? items.reduce((sum, item) => {
                    if (!item?.productDetails) return sum;
                    return sum + (item.productDetails.price || 0) * (item.quantity || 1);
                }, 0) : 0;
            },
            getTotalSavings: () => {
                const items = get().items;
                return Array.isArray(items) ? items.reduce((sum, item) => {
                    if (!item?.productDetails) return sum;
                    const price = item.productDetails.price || 0;
                    const compareAt = item.productDetails.compare_at_price || 0;
                    return sum + (compareAt - price) * (item.quantity || 1);
                }, 0) : 0;
            },
        }),
        {
            name: 'cart-storage',
            // Add onRehydrateStorage to clean up bad state if found
            onRehydrateStorage: () => (state) => {
                if (!state) return;
                if (!Array.isArray(state.items)) {
                    console.warn('Detected corrupted cart items state, resetting to empty array');
                    state.items = [];
                }
                // No request survives a reload, so a persisted flag would only ever
                // restore as a spinner that never stops.
                state.pendingQuantityLines = {};
            }
        }
    )
);

/** Subscribe a single line to its own busy flag, so one line's sync never re-renders the rest. */
export function useIsCartLineQuantityPending(productId: string, variantId?: string | null): boolean {
    return useCartStore((state) => !!state.pendingQuantityLines[cartLineKey(productId, variantId)]);
}

if (typeof window !== 'undefined') {
    window.addEventListener('userLogout', () => {
        useCartStore.getState().resetCartLocal();
    });
}